import React, { useState } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';

const SVGElement = ({ element, style, renderAsMask, bannerSizes, selectedBannerIndex }) => {
  const [keepAspectRatio, setKeepAspectRatio] = useState(true);

  // Add aspect ratio toggle button
  const AspectRatioToggle = () => (
    <div 
      className="aspect-ratio-toggle"
      onClick={(e) => {
        e.stopPropagation();
        setKeepAspectRatio(!keepAspectRatio);
      }}
    >
      {keepAspectRatio ? <LinkIcon /> : <LinkOffIcon />}
    </div>
  );

  // Parse gradient string into gradient object
  const parseGradient = (gradientString) => {
    if (!gradientString.includes('gradient')) return null;

    // Parse linear gradient
    if (gradientString.includes('linear-gradient')) {
      const match = gradientString.match(/linear-gradient\((\d+)deg,\s*(.+)\)/);
      if (!match) return null;

      const angle = parseInt(match[1]);
      const colors = match[2].split(',').map(part => {
        const [color, position] = part.trim().split(' ');
        return {
          color: color,
          offset: position ? parseInt(position) : null
        };
      });

      // Sort stops by offset to ensure left-to-right order
      const stops = colors.map((stop, index, array) => ({
        color: stop.color,
        offset: stop.offset !== null ? stop.offset : (index / (array.length - 1)) * 100
      })).sort((a, b) => a.offset - b.offset);

      return {
        type: 'linear',
        angle,
        stops
      };
    }

    // Parse radial gradient
    if (gradientString.includes('radial-gradient')) {
      const match = gradientString.match(/radial-gradient\((.+)\)/);
      if (!match) return null;

      const colors = match[1].split(',').map(part => {
        const [color, position] = part.trim().split(' ');
        return {
          color: color,
          offset: position ? parseInt(position) : null
        };
      });

      // Sort stops by offset
      const stops = colors.map((stop, index, array) => ({
        color: stop.color,
        offset: stop.offset !== null ? stop.offset : (index / (array.length - 1)) * 100
      })).sort((a, b) => a.offset - b.offset);

      return {
        type: 'radial',
        stops
      };
    }

    return null;
  };

  // Get fill style with gradient
  const getFillStyle = () => {
    if (!element.style) return '#000000';

    if (element.style.fillType === 'solid') {
      return element.style.fill || '#000000';
    }

    // Return gradient reference
    const gradientId = `gradient-${element.id}`;
    return `url(#${gradientId})`;
  };

  // Get stroke style
  const getStrokeStyle = () => {
    if (!element.style?.strokeStyle) return 'none';
    
    switch (element.style.strokeStyle) {
      case 'dashed':
        return `${element.style.strokeWidth * 2},${element.style.strokeWidth}`;
      case 'dotted':
        return `${element.style.strokeWidth},${element.style.strokeWidth}`;
      default:
        return 'none';
    }
  };

  const renderShape = () => {
    const shapeStyles = {
      fill: getFillStyle(),
      stroke: element.style?.stroke || 'none',
      strokeWidth: element.style?.strokeWidth || 0,
      strokeDasharray: getStrokeStyle(),
      opacity: element.style?.opacity || 1,
      visibility: element.style?.visibility || 'visible'
    };

    // Calculate the actual dimensions accounting for stroke width
    const strokeWidth = element.style?.strokeWidth || 0;

    switch (element.shapeType) {
      case 'rectangle':
        return (
          <rect
            x={strokeWidth / 2}
            y={strokeWidth / 2}
            width={element.size.width - strokeWidth}
            height={element.size.height - strokeWidth}
            rx={element.style?.cornerRadius || 0}
            ry={element.style?.cornerRadius || 0}
            style={shapeStyles}
          />
        );
      case 'circle':
        const diameter = Math.min(element.size.width, element.size.height) - strokeWidth;
        return (
          <circle
            cx={element.size.width / 2}
            cy={element.size.height / 2}
            r={(diameter) / 2}
            style={shapeStyles}
          />
        );
      case 'triangle':
        const points = `${strokeWidth/2},${element.size.height - strokeWidth/2} ${element.size.width/2},${strokeWidth/2} ${element.size.width - strokeWidth/2},${element.size.height - strokeWidth/2}`;
        return (
          <polygon
            points={points}
            style={shapeStyles}
          />
        );
      default:
        return null;
    }
  };

  // Create gradient definition
  const createGradientDef = () => {
    if (!element.style?.fillType || element.style.fillType === 'solid') return null;

    const gradientId = `gradient-${element.id}`;
    const stops = element.style.gradientStops || [
      { color: '#000000', offset: 0, opacity: 1 },
      { color: '#ffffff', offset: 100, opacity: 1 }
    ];

    if (element.style.fillType === 'linear') {
      return (
        <linearGradient
          id={gradientId}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
          gradientTransform={`rotate(${element.style.gradientAngle || 0}, 0.5, 0.5)`}
        >
          {stops.map((stop, index) => (
            <stop
              key={index}
              offset={`${stop.offset}%`}
              stopColor={stop.color}
              stopOpacity={stop.opacity}
            />
          ))}
        </linearGradient>
      );
    }

    if (element.style.fillType === 'radial') {
      return (
        <radialGradient
          id={gradientId}
          cx="50%"
          cy="50%"
          r="50%"
        >
          {stops.map((stop, index) => (
            <stop
              key={index}
              offset={`${stop.offset}%`}
              stopColor={stop.color}
              stopOpacity={stop.opacity}
            />
          ))}
        </radialGradient>
      );
    }

    return null;
  };

  return (
    <svg
      key={`${element.id}-${element.style?.fillType || 'solid'}`}
      width={`${element.size.width}px`}
      height={`${element.size.height}px`}
      viewBox={`0 0 ${element.size.width} ${element.size.height}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ 
        display: 'block',
        overflow: 'visible',
        visibility: element.style?.visibility || 'visible'
      }}
    >
      <defs>
        {createGradientDef()}
      </defs>
      
      {bannerSizes && bannerSizes[selectedBannerIndex]?.elements.some(el => 
        el.type === 'image' && el.style?.maskId === element.id
      ) ? (
        <mask id={`${element.layerId}Mask`}>
          {renderShape()}
        </mask>
      ) : (
        renderShape()
      )}
    </svg>
  );
};

export default SVGElement; 